import { useEffect, useRef, useState } from "react";
import { Check, Minus, Star } from "lucide-react";
import { track } from "@/lib/analytics";

const OPTIONS = [
  {
    id: "free",
    name: "Free Account",
    price: "€0",
    note: "no card required",
    cta: { label: "Create account", to: "/portal/register" },
  },
  {
    id: "premium",
    name: "Premium",
    price: "€19",
    note: "per month · €149 per year",
    highlight: false,
    cta: { label: "Go Premium", to: "/portal/register?checkout=premium_monthly" },
  },
  {
    id: "initial",
    name: "Initial Consultation",
    price: "€299",
    note: "60 minutes · incl. VAT",
    flagship: true,
    badge: "Start here",
    cta: { label: "Book initial session", booking: true, testid: "initial-session-btn" },
  },
  {
    id: "followup",
    name: "Follow-up Session",
    price: "€149",
    note: "30 minutes · incl. VAT",
    cta: { label: "Book follow-up", booking: true, testid: "followup-session-btn" },
  },
];

const ROWS = [
  {
    label: "What you get",
    values: {
      free: "Online client portal",
      premium: "Online client portal, unlocked",
      followup: "Live time with a behavioural scientist",
      initial: "Live time with a behavioural scientist",
    },
  },
  {
    label: "Turnaround",
    values: { free: "Instant", premium: "Instant", followup: "Next open slot", initial: "Next open slot" },
  },
  {
    label: "Big Five + Leader D3 assessments",
    values: { free: true, premium: true, followup: "Reviewed with you", initial: true },
  },
  {
    label: "Private incident log",
    values: { free: "5 per month", premium: "Unlimited", followup: false, initial: false },
  },
  {
    label: "AI advisor chat",
    values: { free: true, premium: "Priority", followup: false, initial: false },
  },
  {
    label: "Vulnerability report",
    values: { free: "Summary", premium: "Full", followup: false, initial: false },
  },
  {
    label: "Personalised counter-strategies",
    values: { free: false, premium: true, followup: "Live", initial: "Live" },
  },
  {
    label: "Quarterly progress tracking",
    values: { free: true, premium: true, followup: false, initial: false },
  },
  {
    label: "Written case file from the clinic",
    values: { free: false, premium: false, followup: "+€175 add-on", initial: "+€175 add-on" },
  },
  {
    label: "Live session with a behavioural scientist",
    values: { free: false, premium: false, followup: "30 min", initial: "60 min" },
  },
  {
    label: "HR & legal considerations reviewed",
    values: { free: false, premium: false, followup: false, initial: true },
  },
  {
    label: "Summary document afterwards",
    values: { free: false, premium: "PDF export", followup: true, initial: true },
  },
  {
    label: "Best for",
    values: {
      free: "Working out what you're dealing with",
      premium: "Handling it week by week",
      followup: "Keeping momentum after your first session",
      initial: "Getting a plan for a situation that matters",
    },
    muted: true,
  },
];

const Cell = ({ value, flagship }) => {
  if (value === true) return <Check className="w-[18px] h-[18px] text-[#D4AF37] mx-auto" />;
  if (value === false) return <Minus className="w-4 h-4 text-slate-700 mx-auto" />;
  return <span className={`text-sm ${flagship ? "text-white" : "text-slate-300"}`}>{value}</span>;
};

export const OptionsComparison = ({ onBook }) => {
  const ref = useRef(null);
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const isAnnual = billingPeriod === "annual";
  const premiumPrice = isAnnual ? "€149" : "€19";
  const premiumNote = isAnnual ? "per year · save €79" : "per month · €149/yr";
  const premiumCheckout = isAnnual
    ? "/portal/register?checkout=premium_annual"
    : "/portal/register?checkout=premium_monthly";

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          track("options_table_viewed");
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Shared CTA renderer so the table and the mobile cards stay in sync.
  const renderCta = (opt, mobile = false) => {
    const suffix = mobile ? "-m" : "";
    if (opt.cta.booking) {
      return (
        <button
          onClick={() => {
            track("option_cta_clicked", { option: opt.id, price: opt.price });
            onBook();
          }}
          data-testid={`${opt.cta.testid}${suffix}`}
          className={`w-full py-3 px-3 text-[11px] uppercase tracking-[0.16em] font-medium transition-colors ${
            opt.flagship
              ? "bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F]"
              : "border border-slate-600 text-slate-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
          }`}
        >
          {opt.cta.label}
        </button>
      );
    }
    return (
      <a
        href={opt.id === "premium" ? premiumCheckout : opt.cta.to}
        data-testid={`product-${opt.id}-cta${suffix}`}
        onClick={() =>
          track("option_cta_clicked", {
            option: opt.id,
            price: opt.id === "premium" ? premiumPrice : opt.price,
            period: opt.id === "premium" ? billingPeriod : undefined,
          })
        }
        className="block text-center border border-slate-600 text-slate-300 py-3 px-3 text-[11px] uppercase tracking-[0.16em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
      >
        {opt.cta.label}
      </a>
    );
  };

  const PremiumToggle = ({ mobile = false }) => {
    const suffix = mobile ? "-m" : "";
    return (
      <div
        className="inline-flex items-center rounded-full border border-slate-700 p-0.5"
        data-testid={`premium-billing-toggle${suffix}`}
      >
        <button
          type="button"
          onClick={() => setBillingPeriod("monthly")}
          data-testid={`premium-billing-monthly${suffix}`}
          className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] rounded-full transition-colors ${
            !isAnnual ? "bg-[#D4AF37] text-slate-950" : "text-slate-400 hover:text-slate-200"
          }`}
        >
          Monthly
        </button>
        <button
          type="button"
          onClick={() => setBillingPeriod("annual")}
          data-testid={`premium-billing-annual${suffix}`}
          className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] rounded-full transition-colors ${
            isAnnual ? "bg-[#D4AF37] text-slate-950" : "text-slate-400 hover:text-slate-200"
          }`}
        >
          Annual
        </button>
      </div>
    );
  };

  return (
    <div data-testid="options-comparison" ref={ref}>
      {/* Desktop / tablet: full comparison table */}
      <div className="hidden md:block overflow-x-auto border border-slate-800 bg-slate-900/60">
        <table className="w-full min-w-[780px] border-collapse">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-slate-900 text-left p-5 align-bottom w-[240px]">
                <span className="text-[10px] uppercase tracking-[0.24em] text-slate-500">Compare every option</span>
              </th>
              {OPTIONS.map((opt) => (
                <th
                  key={opt.id}
                  data-testid={`product-${opt.id}`}
                  className={`p-5 align-bottom text-center border-l border-slate-800 ${
                    opt.flagship ? "bg-[#D4AF37]/[0.07]" : ""
                  }`}
                >
                  {opt.badge && (
                    <span className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-[#D4AF37] border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-2 py-1 mb-3">
                      <Star className="w-2.5 h-2.5" /> {opt.badge}
                    </span>
                  )}
                  <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 mb-3">{opt.name}</p>
                  {opt.id === "premium" ? (
                    <>
                      <div className="mb-2">
                        <PremiumToggle />
                      </div>
                      <p className="price-tag text-[#D4AF37] text-2xl font-medium leading-none" data-testid="premium-price">{premiumPrice}</p>
                      <p className="text-slate-500 text-[11px] mt-2 font-normal normal-case tracking-normal">{premiumNote}</p>
                    </>
                  ) : (
                    <>
                      <p className="price-tag text-[#D4AF37] text-2xl font-medium leading-none">{opt.price}</p>
                      <p className="text-slate-500 text-[11px] mt-2 font-normal normal-case tracking-normal">{opt.note}</p>
                    </>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => (
              <tr key={row.label} className="border-t border-slate-800">
                <td className="sticky left-0 z-10 bg-slate-900 p-4 text-slate-300 text-sm align-middle">{row.label}</td>
                {OPTIONS.map((opt) => (
                  <td
                    key={opt.id}
                    className={`p-4 text-center border-l border-slate-800 align-middle ${
                      opt.flagship ? "bg-[#D4AF37]/[0.07]" : ""
                    } ${row.muted ? "text-slate-400" : ""}`}
                  >
                    <Cell value={row.values[opt.id]} flagship={opt.flagship} />
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-t border-slate-700 bg-slate-900/70">
              <td className="sticky left-0 z-10 bg-slate-900 p-5" />
              {OPTIONS.map((opt) => (
                <td key={opt.id} className={`p-5 border-l border-slate-800 ${opt.flagship ? "bg-[#D4AF37]/[0.07]" : ""}`}>
                  {renderCta(opt)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Mobile: each option becomes a self-contained card (no sideways scroll) */}
      <div className="md:hidden space-y-4" data-testid="options-comparison-mobile">
        {OPTIONS.map((opt) => {
          const isPrem = opt.id === "premium";
          const price = isPrem ? premiumPrice : opt.price;
          const note = isPrem ? premiumNote : opt.note;
          return (
            <div
              key={opt.id}
              data-testid={`product-mobile-${opt.id}`}
              className={`border p-5 ${
                opt.flagship ? "border-[#D4AF37]/50 bg-[#D4AF37]/[0.06]" : "border-slate-800 bg-slate-900/60"
              }`}
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  {opt.badge && (
                    <span className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-[#D4AF37] border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-2 py-1 mb-2">
                      <Star className="w-2.5 h-2.5" /> {opt.badge}
                    </span>
                  )}
                  <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">{opt.name}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="price-tag text-[#D4AF37] text-2xl font-medium leading-none">{price}</p>
                  <p className="text-slate-500 text-[11px] mt-1 normal-case tracking-normal">{note}</p>
                </div>
              </div>

              {isPrem && (
                <div className="mb-4">
                  <PremiumToggle mobile />
                </div>
              )}

              <dl className="border-y border-slate-800 divide-y divide-slate-800/70 mb-5">
                {ROWS.map((row) => {
                  const v = row.values[opt.id];
                  return (
                    <div key={row.label} className="flex items-start justify-between gap-4 py-2.5">
                      <dt className="text-slate-400 text-xs leading-snug flex-1">{row.label}</dt>
                      <dd className={`text-right text-sm max-w-[55%] ${row.muted ? "text-slate-400" : "text-slate-200"}`}>
                        {v === true ? (
                          <Check className="w-[18px] h-[18px] text-[#D4AF37] ml-auto" />
                        ) : v === false ? (
                          <Minus className="w-4 h-4 text-slate-700 ml-auto" />
                        ) : (
                          v
                        )}
                      </dd>
                    </div>
                  );
                })}
              </dl>

              {renderCta(opt, true)}
            </div>
          );
        })}
      </div>

      <p className="text-slate-500 text-xs mt-4 max-w-3xl leading-relaxed" data-testid="options-comparison-footnote">
        Need everything written up for HR or a lawyer? The clinic prepares a full written case file as a €175 add-on to any
        session — just ask during your call.
      </p>
    </div>
  );
};

export default OptionsComparison;
